import { useState, useEffect } from 'react';
import { api, PanelHowTo } from './_barrel.js';


        // Analyst-triggered Pipl identity lookup for a subject. On-demand only
        // (Pipl bills per match), so nothing runs until the analyst clicks. The
        // most recent stored lookup is re-hydrated on mount (no billed call),
        // and the analyst can opt a lookup into the exposure report.
        function PiplLookupPanel({ token, subjectId }) {
            const [state, setState] = useState('idle');   // idle | loading | done | error
            const [data, setData] = useState(null);
            const [err, setErr] = useState(null);
            const [savingInclude, setSavingInclude] = useState(false);
            const [email, setEmail] = useState('');
            const [phone, setPhone] = useState('');
            const [collapsed, setCollapsed] = useState(() => {
                try { return localStorage.getItem('veil_pipl_collapsed') === '1'; } catch (e) { return false; }
            });
            const toggleCollapsed = () => setCollapsed(v => {
                const next = !v;
                try { localStorage.setItem('veil_pipl_collapsed', next ? '1' : '0'); } catch (e) {}
                return next;
            });

            // Re-hydrate the last stored lookup so a refresh doesn't lose it and
            // doesn't re-spend a credit.
            useEffect(() => {
                let live = true;
                (async () => {
                    try {
                        const r = await api(`/api/v1/subjects/${subjectId}/pipl-lookup`, {}, token);
                        if (live && r) { setData(r); setState('done'); }
                    } catch (e) { /* no prior lookup / not configured — stay idle */ }
                })();
                return () => { live = false; };
            }, [subjectId, token]);

            async function run() {
                setState('loading'); setErr(null);
                try {
                    // Analyst-entered email/phone override the on-file identifiers;
                    // omit the body entirely to fall back to what's on file.
                    const q = {};
                    if (email.trim()) q.email = email.trim();
                    if (phone.trim()) q.phone = phone.trim();
                    const opts = { method: 'POST' };
                    if (Object.keys(q).length) opts.body = JSON.stringify(q);
                    const r = await api(`/api/v1/subjects/${subjectId}/pipl-lookup`, opts, token);
                    setData(r); setState('done');
                } catch (e) {
                    if (e.status === 503) setErr('Pipl is not configured on this deployment (no API key).');
                    else setErr((e.data && e.data.detail) || e.message);
                    setState('error');
                }
            }

            async function toggleInclude() {
                if (!data || !data.id) return;
                const next = !data.include_in_report;
                setSavingInclude(true);
                try {
                    const updated = await api(
                        `/api/v1/subjects/${subjectId}/pipl-lookup/${data.id}`,
                        { method: 'PATCH', body: JSON.stringify({ include_in_report: next }) },
                        token);
                    setData(updated);
                } catch (e) {
                    setErr((e.data && e.data.detail) || e.message);
                } finally {
                    setSavingInclude(false);
                }
            }

            // Persist the analyst-curated person list (pruned of irrelevant
            // people/fields). Only what's kept flows into the report.
            async function patchPersons(nextPersons) {
                if (!data || !data.id) return;
                try {
                    const updated = await api(
                        `/api/v1/subjects/${subjectId}/pipl-lookup/${data.id}`,
                        { method: 'PATCH', body: JSON.stringify({ persons: nextPersons }) }, token);
                    setData(updated);
                } catch (e) { setErr((e.data && e.data.detail) || e.message); }
            }
            const removePerson = (idx) => patchPersons((data.persons || []).filter((_, i) => i !== idx));
            const removeChip = (personIdx, field, value) => patchPersons(
                (data.persons || []).map((p, i) =>
                    i === personIdx ? { ...p, [field]: (p[field] || []).filter(v => v !== value) } : p));

            // When `field` is given, each chip carries an ✕ to drop that value.
            const chips = (label, arr, field, personIdx) => (arr && arr.length > 0) && (
                <div className="mb-2">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-1">{label}</div>
                    <div className="flex flex-wrap gap-2">
                        {arr.map((x, i) => (
                            <span key={i} className="chip" style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {x}
                                {field !== undefined && (
                                    <button onClick={() => removeChip(personIdx, field, x)} title="Remove — not relevant"
                                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 12 }}>✕</button>
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            );

            return (
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex items-center justify-between">
                        <span className="font-display text-xl gold" style={{ cursor: 'pointer' }}
                              onClick={toggleCollapsed}>
                            Identity lookup · Pipl
                            <span className="text-xs text-muted ml-2">{collapsed ? '▸' : '▾'}</span>
                        </span>
                        <div className="flex items-center gap-3">
                            {!collapsed && (
                                <button className="btn-gold" style={{ padding: '6px 14px', fontSize: 13 }}
                                        onClick={run} disabled={state === 'loading'}>
                                    {state === 'loading' ? 'Searching…' : (state === 'done' ? 'Search again' : 'Look up identity')}
                                </button>
                            )}
                            <button className="text-xs font-mono uppercase tracking-widest text-muted"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                    onClick={toggleCollapsed}>
                                {collapsed ? '+ Show' : '– Hide'}
                            </button>
                        </div>
                    </div>
                    <PanelHowTo id="pipl" />
                    {!collapsed && (
                    <div className="px-6 py-5">
                        <div className="flex flex-wrap gap-2 mb-2" style={{ maxWidth: 640 }}>
                            <input value={email} onChange={e => setEmail(e.target.value)} type="email"
                                   placeholder="Email to search (optional)"
                                   style={{ flex: '1 1 220px', minWidth: 0, background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '6px 10px', fontSize: 13 }} />
                            <input value={phone} onChange={e => setPhone(e.target.value)} type="tel"
                                   placeholder="Phone to search (optional)"
                                   style={{ flex: '1 1 180px', minWidth: 0, background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '6px 10px', fontSize: 13 }} />
                        </div>
                        <div className="text-xs text-muted mb-4">
                            Enter an email and/or phone to search on. Leave blank to use the subject's email or social handle on file.
                        </div>
                        {state === 'idle' && (
                            <div className="text-sm text-muted" style={{ maxWidth: 640 }}>
                                Resolve the subject against Pipl's people-data index — surfacing alternate names,
                                other emails and phones, addresses, jobs and social profiles. Runs on demand only.
                                Review results before acting; nothing appears in the client report until you tick
                                "Include in exposure report".
                            </div>
                        )}
                        {state === 'error' && <div className="text-sm" style={{ color: 'var(--red)' }}>{err}</div>}
                        {state === 'done' && data && (
                            <>
                                <div className="text-sm text-muted mb-4">
                                    {data.persons_count === 0
                                        ? 'No match found for the identifier searched.'
                                        : `${data.persons.length} ${data.persons.length === 1 ? 'identity' : 'possible identities'} returned` +
                                          (data.searched.email ? ` · searched ${data.searched.email}`
                                            : data.searched.phone ? ` · searched ${data.searched.phone}`
                                            : data.searched.username ? ` · searched @${data.searched.username}` : '')}
                                </div>
                                {(data.warnings || []).map((w, i) => (
                                    <div key={i} className="text-xs mb-2" style={{ color: 'var(--amber)' }}>⚠ {w}</div>
                                ))}
                                {data.persons.map((p, i) => (
                                    <div key={i} className="mb-5 pb-5" style={{ borderBottom: i < data.persons.length - 1 ? '1px solid var(--line)' : 'none' }}>
                                        <div className="flex items-center gap-3 mb-3">
                                            {p.images && p.images[0] &&
                                                <img src={p.images[0]} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--line)' }} />}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="text-sm" style={{ color: 'var(--text)' }}>{(p.names && p.names[0]) || 'Unnamed match'}</div>
                                                <div className="text-xs text-muted">
                                                    {p.match_pct}% match
                                                    {p.gender ? ` · ${p.gender}` : ''}{p.dob ? ` · ${p.dob}` : ''}
                                                    {p.origin_countries && p.origin_countries.length ? ` · ${p.origin_countries.join(', ')}` : ''}
                                                </div>
                                            </div>
                                            <button onClick={() => removePerson(i)} title="Remove this person — not the subject"
                                                    className="text-xs" style={{ background: 'none', border: '1px solid var(--line)', borderRadius: 4, color: 'var(--red)', cursor: 'pointer', padding: '2px 8px', flexShrink: 0 }}>
                                                ✕ Not relevant
                                            </button>
                                        </div>
                                        {chips('Also known as', (p.names || []).slice(1), 'names', i)}
                                        {chips('Emails', p.emails, 'emails', i)}
                                        {chips('Phones', p.phones, 'phones', i)}
                                        {chips('Usernames', p.usernames, 'usernames', i)}
                                        {chips('Addresses', p.addresses, 'addresses', i)}
                                        {chips('Jobs', p.jobs, 'jobs', i)}
                                        {chips('Education', p.educations, 'educations', i)}
                                        {chips('Relationships', p.relationships, 'relationships', i)}
                                        {p.social_urls && p.social_urls.length > 0 && (
                                            <div className="mb-2">
                                                <div className="font-mono text-xs uppercase tracking-widest text-muted mb-1">Profiles</div>
                                                <div className="flex flex-col gap-1">
                                                    {p.social_urls.map((u, j) => (
                                                        <a key={j} href={u} target="_blank" rel="noopener noreferrer"
                                                           className="text-xs" style={{ color: 'var(--cyan)', wordBreak: 'break-all' }}>{u}</a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {data.id && data.persons_count > 0 && (
                                    <label className="flex items-center gap-2 text-sm mt-2"
                                           style={{ cursor: savingInclude ? 'wait' : 'pointer' }}>
                                        <input type="checkbox" checked={!!data.include_in_report}
                                               onChange={toggleInclude} disabled={savingInclude} />
                                        <span style={{ color: 'var(--text)' }}>
                                            Include in exposure report
                                        </span>
                                        <span className="text-xs text-muted">
                                            — adds an identity-aggregation exposure finding to § III of the PDF.
                                        </span>
                                    </label>
                                )}
                            </>
                        )}
                    </div>
                    )}
                </div>
            );
        }

export { PiplLookupPanel };
